import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { BaseView, BaseText, BaseSafeFormView } from '@/components/base'
import { AppButton } from '@/components/AppButton'
import { CardItem } from '@/components/CardItem'
import { AddressSelector } from '@/components/AddressSelector'
import { SelectedAddressDisplay } from '@/components/SelectedAddressDisplay'
import { NetworkSelector } from '@/features/currency-deposit/components'
import { currencyUtils } from '@/utils'
import { useTheme } from '@/hooks/useTheme'
import type { WalletCurrency } from '@/domain'
import { StickyHeaderNavigation } from '../StickyHeaderNavigation'
import { PaymentMethodSelectorWithModal } from '../PaymentMethodSelector/PaymentMethodSelectorWithModal'
import { PaymentMethodSelectorTransferIn } from '../PaymentMethodSelector/PaymentMethodSelectorTransferIn'
import {
  PaymentMethodsType,
  paymentMethodsList,
} from '@/constants/paymentMethods'
import useOffsets from '@/hooks/useOffsets'
import { SmallInfo } from '@/assets/icons/SmallInfo'
import { APYInfoModal } from '@/components/APYInfoModal'
import { Separator } from '@/components/Separator'
import { useState } from 'react'
import { SHOW_APPLE_PAY } from '@/config/app'

const { formatDynamicCurrency } = currencyUtils

interface ExternalWalletAddress {
  id?: string
  name?: string
  address: string
}

interface BlockchainInfo {
  id: number
  name: string
  code: string
  icon?: string
}

interface ConfirmTransactionTemplateProps {
  screenTitle: string
  buttonTitle: string
  amountInCrypto?: string
  amount?: {
    amount: string
    currency: string
  }
  transactionFee?: {
    amount: string
    currency: string
  }
  total?: {
    amount: string
    currency: string
  }
  onSubmit: () => void
  currency?: WalletCurrency
  isLoading?: boolean
  isLoadingSubmit?: boolean
  byScreen?: 'withdraw' | 'addMoney' | 'sell' | 'transferIn'
  selectedBlockchain?: BlockchainInfo
  onSelectBlockchain?: (blockchain: BlockchainInfo) => void
  externalWalletAddress?: ExternalWalletAddress
  buttonDisabled?: boolean
  transactionFeeIsLoading?: boolean
  infoCurrency?: string
  totalRelative?: string
  savedAddressesCount?: number
  isLoadingAddresses?: boolean
  // Payment method props for add-money
  selectedPaymentMethod?: PaymentMethodsType
  onSelectPaymentMethod?: (paymentMethod: PaymentMethodsType) => void
  _shouldShowWallet?: boolean
  _shouldShowOnRampMethods?: boolean
  // Transfer-in specific props
  estimatedEarnings?: string
  annualReturn?: string
  AvatarCurrencyCustom?: React.ReactNode
  // Custom back navigation
  onBackPress?: () => void
  // Custom render before button (e.g. warnings, notices)
  renderBeforeButton?: React.ReactNode
}

interface ConfirmationItemProps {
  label: string
  value: string
  isLoading?: boolean
  isTotalAmount?: boolean
  totalRelative?: string
  IconComponent?: React.ReactNode
  isFee?: boolean
}

function ConfirmationItem({
  label,
  value,
  isLoading = false,
  isTotalAmount = false,
  totalRelative,
  IconComponent,
  isFee = false,
}: ConfirmationItemProps) {
  const theme = useTheme()

  if (isLoading) {
    return (
      <BaseView
        p={16}
        rounded={12}
        style={{ backgroundColor: theme.colors.background.cardItemRoot }}
      >
        <BaseView
          style={{
            height: '20px',
            backgroundColor: '#374151',
            borderRadius: '4px',
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
        />
      </BaseView>
    )
  }

  return (
    <BaseView
      // p={16}
      rounded={12}
      // style={{ backgroundColor: theme.colors.background.cardItemRoot }}
    >
      <BaseView row justify="space-between" align="center">
        <BaseView row align="center" gap={8}>
          <BaseText
            fontSize={isTotalAmount ? 12 : 18}
            fontFamily={isTotalAmount ? 'semibold' : 'regular'}
            color="label.description"
          >
            {label}
          </BaseText>
          {IconComponent}
        </BaseView>
        <BaseView align="flex-end">
          <BaseText
            fontSize={isTotalAmount ? 20 : 16}
            fontFamily={isTotalAmount ? 'bold' : 'medium'}
            color={isFee ? 'label.disabled' : 'label.default'}
          >
            {value}
          </BaseText>
          {totalRelative && (
            <BaseText
              fontSize={12}
              fontFamily="regular"
              color="label.description"
            >
              {totalRelative}
            </BaseText>
          )}
        </BaseView>
      </BaseView>
    </BaseView>
  )
}

function CryptoAddressAndNetwork({
  selectedBlockchain,
  onSelectBlockchain,
  externalWalletAddress,
  isLoading = false,
  savedAddressesCount = 0,
  isLoadingAddresses = false,
}: {
  selectedBlockchain?: BlockchainInfo
  onSelectBlockchain?: (blockchain: BlockchainInfo) => void
  externalWalletAddress?: ExternalWalletAddress
  isLoading?: boolean
  savedAddressesCount?: number
  isLoadingAddresses?: boolean
}) {
  const navigate = useNavigate()
  if (isLoading) {
    return (
      <BaseView gap={20}>
        <AddressSelector isLoading={true} />
        <BaseView
          style={{
            height: '76px',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '34px',
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
        />
      </BaseView>
    )
  }

  const hasSelectedAddress = !!(
    externalWalletAddress?.address && externalWalletAddress.address.trim()
  )

  return (
    <BaseView gap={20}>
      {/* Selected Address Display - показываем только если адрес выбран */}
      {hasSelectedAddress && (
        <SelectedAddressDisplay
          id={externalWalletAddress?.id}
          name={externalWalletAddress?.name || 'Unnamed'}
          address={externalWalletAddress.address}
          onPress={() => {
            // Навигация к странице выбора адреса
            navigate('/withdraw/choose-address')
          }}
        />
      )}

      {/* Address Selection - показываем только если адрес НЕ выбран */}
      {!hasSelectedAddress && (
        <AddressSelector
          externalWalletAddress={externalWalletAddress}
          blockchainCode={selectedBlockchain?.code}
          isLoading={isLoading}
          savedAddressesCount={savedAddressesCount}
          isLoadingAddresses={isLoadingAddresses}
        />
      )}

      {/* Network Selection */}
      {selectedBlockchain && (
        <NetworkSelector
          blockchain={selectedBlockchain}
          onPress={() => onSelectBlockchain?.(selectedBlockchain)}
          isLoading={isLoading}
        />
      )}
    </BaseView>
  )
}

function CurrencyAvatarAndInfo({
  currency,
  amountInCrypto,
  isLoading = false,
  infoCurrency,
  AvatarCurrencyCustom,
}: {
  currency?: WalletCurrency
  amountInCrypto?: string
  isLoading?: boolean
  infoCurrency?: string
  AvatarCurrencyCustom?: React.ReactNode
}) {
  const formattedAmountInCrypto = formatDynamicCurrency({
    amount: Number(amountInCrypto || 0),
    currencyCode: currency?.code || 'USDT',
    showDecimals: true,
  })
  // console.log('formattedAmountInCrypto', formattedAmountInCrypto)

  if (isLoading) {
    return (
      <BaseView center gap={16} mb={32}>
        <BaseView
          style={{
            width: '80px',
            height: '80px',
            backgroundColor: '#374151',
            borderRadius: '40px',
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
        />
        <BaseView gap={8}>
          <BaseView
            style={{
              width: '120px',
              height: '32px',
              backgroundColor: '#374151',
              borderRadius: '4px',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
          <BaseView
            style={{
              width: '80px',
              height: '20px',
              backgroundColor: '#374151',
              borderRadius: '4px',
              animation: 'pulse 1.5s ease-in-out infinite',
              margin: '0 auto',
            }}
          />
        </BaseView>
      </BaseView>
    )
  }

  return (
    <BaseView center gap={16} mb={32}>
      {AvatarCurrencyCustom ? (
        AvatarCurrencyCustom
      ) : (
        <CardItem.CurrencyIcon
          uri={currency?.icon}
          bgColor={currency?.avatarBGRefColor || '#6366F1'}
          size={80}
          fallbackText={currency?.code?.substring(0, 2) || '??'}
        />
      )}
      <BaseView center gap={4}>
        <BaseText fontSize={28} fontFamily="bold" color="label.default">
          {formattedAmountInCrypto}
        </BaseText>
        {infoCurrency && (
          <BaseText
            fontSize={16}
            fontFamily="regular"
            color="label.description"
          >
            {infoCurrency}
          </BaseText>
        )}
      </BaseView>
    </BaseView>
  )
}

export const ConfirmTransactionTemplate: React.FC<
  ConfirmTransactionTemplateProps
> = ({
  screenTitle,
  buttonTitle,
  amountInCrypto,
  transactionFee,
  total,
  onSubmit,
  currency,
  isLoading = false,
  isLoadingSubmit = false,
  byScreen = 'withdraw',
  selectedBlockchain,
  onSelectBlockchain,
  externalWalletAddress,
  buttonDisabled = false,
  transactionFeeIsLoading = false,
  infoCurrency,
  totalRelative,
  savedAddressesCount = 0,
  isLoadingAddresses = false,
  // Payment method props
  selectedPaymentMethod,
  onSelectPaymentMethod,
  _shouldShowWallet = false,
  _shouldShowOnRampMethods = true,
  // Transfer-in specific props
  estimatedEarnings,
  annualReturn,
  AvatarCurrencyCustom,
  // Custom back navigation
  onBackPress,
  // Custom render before button
  renderBeforeButton,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const offsets = useOffsets()
  const [isAPYModalOpen, setIsAPYModalOpen] = useState(false)
  const shouldShowNetworkAndAddress =
    byScreen === 'withdraw' && !!selectedBlockchain
  const shouldShowPaymentMethod =
    (byScreen === 'addMoney' || byScreen === 'transferIn') &&
    onSelectPaymentMethod

  // Фильтруем методы оплаты на основе availableOnRampPaymentMethods
  const filteredPaymentMethods = React.useMemo(() => {
    if (byScreen === 'addMoney' && currency?.availableOnRampPaymentMethods) {
      // Маппинг между форматами
      const methodMapping: Record<string, string> = {
        'apple-pay': 'ApplePay',
        'google-pay': 'GooglePay',
        card: 'Card',
        'bank-transfer': 'OnlineBanking',
      }

      return paymentMethodsList.filter((method) => {
        // Скрываем Apple Pay, если SHOW_APPLE_PAY = false
        if (method.paymentMethod === 'ApplePay' && !SHOW_APPLE_PAY) {
          return false
        }

        // Проверяем, есть ли метод в availableOnRampPaymentMethods
        return currency.availableOnRampPaymentMethods.some(
          (availableMethod) => {
            const mappedMethod =
              methodMapping[availableMethod] || availableMethod
            return method.paymentMethod === mappedMethod
          },
        )
      })
    }
    return paymentMethodsList
  }, [byScreen, currency?.availableOnRampPaymentMethods])

  const formattedTotalAmount = total
    ? formatDynamicCurrency({
        amount: Number(total.amount || 0),
        currencyCode: total.currency || 'USDT',
        showDecimals: true,
      })
    : '0.00 USDT'

  const formattedTransactionFee = transactionFee
    ? formatDynamicCurrency({
        amount: Number(transactionFee.amount || 0),
        currencyCode: transactionFee.currency || 'USDT',
        showDecimals: true,
      })
    : '0.00 USDT'

  return (
    <BaseSafeFormView
      style={{
        paddingTop: 0,
      }}
    >
      <StickyHeaderNavigation title={screenTitle} onBackPress={onBackPress} />

      <BaseView style={{ paddingTop: 170 }} flex={1} pt={24} px={13}>
        <CurrencyAvatarAndInfo
          currency={currency}
          amountInCrypto={amountInCrypto}
          isLoading={isLoading}
          infoCurrency={infoCurrency}
          AvatarCurrencyCustom={AvatarCurrencyCustom}
        />

        <BaseView gap={4} pb={18}>
          {/* Transfer-in specific items */}
          {byScreen === 'transferIn' && estimatedEarnings && (
            <ConfirmationItem
              label={t('translation:transferIn.estimatedEarnings')}
              value={`${estimatedEarnings}%`}
              isLoading={isLoading}
              IconComponent={
                <BaseView
                  onClick={() => setIsAPYModalOpen(true)}
                  style={{ cursor: 'pointer' }}
                >
                  <SmallInfo
                    size={16}
                    color={theme.colors.label.disabled}
                    secondColor={theme.colors.background.screen}
                  />
                </BaseView>
              }
            />
          )}

          {byScreen === 'transferIn' && annualReturn && (
            <ConfirmationItem
              label={t('translation:transferIn.annualReturn')}
              value={`${annualReturn} ${currency?.displayCurrencyCode || 'USDT'}`}
              isLoading={isLoading}
            />
          )}

          {/* Separator line after transfer-in items */}
          {byScreen === 'transferIn' && (estimatedEarnings || annualReturn) && (
            <BaseView style={{ marginTop: 8 }}>
              <Separator />
            </BaseView>
          )}

          {/* Transaction Fee - показываем только если не transferIn */}
          {byScreen !== 'transferIn' && (
            <ConfirmationItem
              label={t('translation:transactionFee')}
              value={formattedTransactionFee}
              isLoading={transactionFeeIsLoading || !transactionFee}
              isFee={true}
            />
          )}

          {/* Total Amount - показываем только если не transferIn */}
          {byScreen !== 'transferIn' && (
            <ConfirmationItem
              label={
                byScreen === 'addMoney'
                  ? t('translation:totalYouWillPay')
                  : t('translation:totalYouWillGet')
              }
              value={formattedTotalAmount}
              isTotalAmount
              totalRelative={totalRelative}
              isLoading={transactionFeeIsLoading || !total}
            />
          )}
        </BaseView>

        <BaseView mt="auto" gap={24}>
          {/* Payment Method Selector */}
          {shouldShowPaymentMethod && byScreen === 'transferIn' && (
            <PaymentMethodSelectorTransferIn
              selectedMethod={selectedPaymentMethod?.paymentMethod || 'Wallet'}
              onMethodChange={(method) => {
                // Найдем полный объект PaymentMethodsType по paymentMethod
                const paymentMethods =
                  currency?.availableOnRampPaymentMethods || []
                const foundMethod = paymentMethods.find((m) => m === method)
                if (foundMethod && onSelectPaymentMethod) {
                  // Создаем объект PaymentMethodsType для Wallet
                  const walletPaymentMethod: PaymentMethodsType = {
                    id: 'wallet',
                    name: 'Wallet',
                    paymentMethod: 'Wallet',
                    displayCurrencyCode:
                      currency?.displayCurrencyCode || 'USDT',
                    currencyName: currency?.name || 'USDT',
                    currencyIconUrl: currency?.icon,
                    currencyBgColor: currency?.avatarBGRefColor || '#171819',
                    formattedAmountBalance: currency?.formattedAmountBalance,
                    iconName: 'wallet',
                    iconSize: 32,
                  }
                  onSelectPaymentMethod(walletPaymentMethod)
                }
              }}
              hasEnoughBalance={true}
              disabled={isLoading}
              shouldDisplayCurrencyIcon={true}
              currencyCode={currency?.code as any}
            />
          )}

          {/* Payment Method Selector for add-money */}
          {shouldShowPaymentMethod && byScreen === 'addMoney' && (
            <PaymentMethodSelectorWithModal
              selectedMethod={selectedPaymentMethod?.paymentMethod || 'Card'}
              onMethodChange={(method) => {
                // PaymentMethodSelectorWithModal передает только paymentMethod string,
                // нужно найти полный объект в списке доступных методов
                if (onSelectPaymentMethod) {
                  const foundMethod = filteredPaymentMethods.find(
                    (m: PaymentMethodsType) => m.paymentMethod === method,
                  )
                  if (foundMethod) {
                    onSelectPaymentMethod(foundMethod)
                  }
                }
              }}
              hasEnoughBalance={true}
              disabled={isLoading}
              paymentMethodsListCustom={filteredPaymentMethods}
              shouldDisplayCurrencyIcon={_shouldShowOnRampMethods === false}
              currencyCode={currency?.code as any}
            />
          )}

          {/* Network and Address for withdraw */}
          {shouldShowNetworkAndAddress && currency && (
            <CryptoAddressAndNetwork
              selectedBlockchain={selectedBlockchain}
              onSelectBlockchain={onSelectBlockchain}
              externalWalletAddress={externalWalletAddress}
              isLoading={isLoading}
              savedAddressesCount={savedAddressesCount}
              isLoadingAddresses={isLoadingAddresses}
            />
          )}

          {/* Custom content before button (e.g. warnings, notices) */}
          {renderBeforeButton && <BaseView>{renderBeforeButton}</BaseView>}

          {/* Submit Button */}
          <BaseView style={{ paddingBottom: offsets.bottom }}>
            <AppButton
              label={buttonTitle}
              onPress={onSubmit}
              disabled={buttonDisabled || isLoading}
              isLoading={isLoadingSubmit}
              type="primaryGradient"
              style={{ width: '100%' }}
            />
          </BaseView>
        </BaseView>
      </BaseView>

      {/* APY Info Modal */}
      <APYInfoModal
        isOpen={isAPYModalOpen}
        onClose={() => setIsAPYModalOpen(false)}
        apyPercentage={
          estimatedEarnings ? parseFloat(estimatedEarnings) : undefined
        }
      />
    </BaseSafeFormView>
  )
}
